export type SendEntry = 'header' | 'confirm'
export type SendInput = {
  entry: SendEntry
  mode: 'automatic' | 'prioritized'
  view: 'calendar' | 'priorities'
  directorGate: boolean
  lessonCount: number
  priorityCount: number
  absenceTypeChosen: boolean
  tooManyLessons: number | null
  tooManyDays: number | null
  pastDate: 'start' | 'end' | 'single' | null
  endBeforeStart: boolean
  lessonAlreadyPast: boolean
  unqualifiedNames: readonly string[]
  qualifiedTeacherSearch?: QualifiedTeacherSearch
  availability?: AvailabilityLoad
}

export type QualifiedTeacherSearch =
  | { status: 'absent' }
  | { status: 'loading' }
  | { status: 'failed'; failure: ApiFailure }
  | { status: 'ready'; count: number }

export type AvailabilityLoad =
  | { status: 'not_needed' }
  | { status: 'loading' }
  | { status: 'failed'; failure: ApiFailure }
  | { status: 'ready' }

export type SendDecision =
  | { outcome: 'senden' }
  | { outcome: 'Review öffnen' }
  | { outcome: 'nächster Schritt' }
  | { outcome: 'warten'; sentence: string }
  | { outcome: 'sperren'; sentence: string }

const LESSON_REQUIRED = 'Bitte wahlen Sie mindestens eine Lektion aus'
const PRIORITY_REQUIRED = 'Bitte wählen Sie mindestens einen Lehrer als Priorität aus (Karte anklicken).'
const ABSENCE_REQUIRED = 'Bitte wählen Sie eine Abwesenheits-Art.'
const END_BEFORE_START = 'Enddatum muss nach Startdatum liegen'
const START_IN_PAST = 'Startdatum darf nicht in der Vergangenheit liegen'
const END_IN_PAST = 'Enddatum darf nicht in der Vergangenheit liegen'
const DATE_IN_PAST = 'Datum darf nicht in der Vergangenheit liegen'
const LESSON_IN_PAST = 'Lektionen in der Vergangenheit können nicht angefragt werden'
const QUALI_WAIT = 'Qualifizierte Lehrpersonen werden noch gesucht. Bitte kurz warten.'
const AVAILABILITY_WAIT = 'Lehrer werden geladen, bitte warten...'
const AVAILABILITY_FAILED = 'Lehrerverfügbarkeit konnte nicht geladen werden'
const NO_QUALIFIED = 'Keine qualifizierten Lehrer für die ausgewählten Lektionen gefunden. ' +
  'Mögliche Ursachen: ' +
  '1) Keine Qualifikationsdaten für die Fächer, ' +
  '2) Alle Lehrer sind zu dieser Zeit verhindert, ' +
  '3) Stundenplan-Einträge haben kein Datum/Wochentag. ' +
  'Bitte kontaktieren Sie den Administrator.'

function block(sentence: string): SendDecision {
  return { outcome: 'sperren', sentence }
}

function wait(sentence: string): SendDecision {
  return { outcome: 'warten', sentence }
}

function qualifiedTeacherSearchBlock(input: SendInput): SendDecision | null {
  if (input.mode !== 'automatic') return null
  const search = input.qualifiedTeacherSearch
  if (!search) return null
  if (search.status === 'ready') return search.count === 0 ? block(NO_QUALIFIED) : null
  if (search.status === 'loading' || search.status === 'absent') return wait(QUALI_WAIT)
  const sentence = classifyApiFailure(search.failure)
  if (sentence == null) return null
  return block(sentence)
}

function availabilityBlock(input: SendInput): SendDecision | null {
  const load = input.availability
  if (!load || load.status === 'not_needed' || load.status === 'ready') return null
  if (load.status === 'loading') return wait(AVAILABILITY_WAIT)
  if (load.status !== 'failed') return null
  const sentence = classifyApiFailure(load.failure)
  if (sentence == null) return null
  return block(`${AVAILABILITY_FAILED} (${sentence})`)
}

function sharedBlock(input: SendInput): SendDecision | null {
  if (input.lessonCount === 0) return block(LESSON_REQUIRED)
  if (input.tooManyLessons != null) return block(`Maximale Anzahl Lektionen: ${input.tooManyLessons}`)
  if (input.endBeforeStart) return block(END_BEFORE_START)
  if (input.pastDate === 'start') return block(START_IN_PAST)
  if (input.pastDate === 'end') return block(END_IN_PAST)
  if (input.tooManyDays != null) return block(`Maximale Anzahl Tage: ${input.tooManyDays}`)
  if (input.pastDate === 'single') return block(DATE_IN_PAST)
  if (input.lessonAlreadyPast) return block(LESSON_IN_PAST)
  if (input.unqualifiedNames.length > 0 && !availabilityFailureHidesUnqualifiedNames(input)) {
    return block(`Folgende Lehrer sind fur die ausgewahlten Lektionen nicht qualifiziert: ${input.unqualifiedNames.join(', ')}`)
  }
  return null
}

function availabilityFailureHidesUnqualifiedNames(input: SendInput): boolean {
  const load = input.availability
  if (!load || load.status !== 'failed') return false
  return classifyApiFailure(load.failure) != null
}

export function headerButtonLabel(input: Pick<SendInput, 'mode' | 'view' | 'directorGate'>): string {
  if (input.mode === 'prioritized' && input.view === 'calendar') return 'Nächster Schritt'
  if (input.mode === 'automatic' && !input.directorGate) return 'Senden'
  return 'Überprüfen & Senden'
}

export function decideSend(input: SendInput): SendDecision {
  const shared = sharedBlock(input)
  if (shared) return shared
  const qualifiedTeacherSearch = qualifiedTeacherSearchBlock(input)
  if (qualifiedTeacherSearch) return qualifiedTeacherSearch
  const availability = availabilityBlock(input)
  if (availability) return availability

  const headerNext = input.entry === 'header' && input.mode === 'prioritized' && input.view === 'calendar'
  if (input.mode === 'prioritized' && !headerNext && input.priorityCount === 0) {
    return block(PRIORITY_REQUIRED)
  }
  if (input.entry === 'confirm' && input.directorGate && !input.absenceTypeChosen) {
    return block(ABSENCE_REQUIRED)
  }
  if (headerNext) return { outcome: 'nächster Schritt' }
  if (input.entry === 'header' && input.mode === 'automatic' && input.directorGate) {
    return { outcome: 'Review öffnen' }
  }
  if (input.entry === 'header' && input.mode === 'prioritized') {
    return { outcome: 'Review öffnen' }
  }
  return { outcome: 'senden' }
}

export type ApiFailure = {
  method: string
  address: string
  status: number | null
  code: string | null
  body: unknown
}

const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT'])
const CANCELLED_CODES = new Set(['ERR_CANCELED', 'ERR_CANCELLED'])

export function classifyApiFailure(failure: ApiFailure): string | null {
  if (CANCELLED_CODES.has(failure.code ?? '')) return null
  if (!hasStatus(failure.status)) {
    return TIMEOUT_CODES.has(failure.code ?? '') ? 'Zeitüberschreitung' : 'Netzwerkfehler'
  }
  return serverSentence(failure.body) ?? `Der Server hat mit Status ${failure.status} geantwortet.`
}

export function apiFailureLine(failure: ApiFailure): string | null {
  const sentence = classifyApiFailure(failure)
  if (sentence == null) return null
  const method = failure.method.trim() ? failure.method.trim().toUpperCase() : '—'
  const address = failure.address.trim() ? failure.address.trim() : '—'
  const status = hasStatus(failure.status) ? String(failure.status) : '—'
  const code = failure.code?.trim() ? failure.code.trim() : '—'
  return `[API] ${method} ${address} ${status} ${code} ${sentence.replace(/\n/g, ' — ')}`
}

function hasStatus(status: number | null): boolean {
  return typeof status === 'number' && status > 0
}

function serverSentence(body: unknown): string | null {
  const record = recordOf(body)
  const detail = textField(record?.detail)
  const message = textField(record?.message)
  const hints = stringList(record?.details)
  if (hints.length > 0) {
    if (message === 'Validierungsfehler' || detail === 'Validierungsfehler' || (!message && !detail)) {
      return ['Validierungsfehler', ...hints].join('\n')
    }
    const headline = message ?? detail
    if (!headline) return hints.join('\n')
    const extra = hints.filter(hint => hint !== headline)
    return extra.length > 0 ? [headline, ...extra].join('\n') : headline
  }
  return detail ?? message
}

function recordOf(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  return body as Record<string, unknown>
}

function textField(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map(item => item.trim())
}
